import { motion } from 'framer-motion';
import { MapPin, Clock, Shield, Plane, Sparkles } from 'lucide-react';

const MASCOT_URL = "https://customer-assets.emergentagent.com/job_b35da085-09af-4a55-863c-9eb3e423a8d1/artifacts/erel0x7n_buzon.png";

// Orden principal: Venezuela, Colombia, Panamá, República Dominicana + (próximamente) Costa Rica, Nicaragua
const destinations = [
  {
    country: 'Venezuela',
    flag: '🇻🇪',
    image: 'https://static.prod-images.emergentagent.com/jobs/b35da085-09af-4a55-863c-9eb3e423a8d1/images/ff76c77e64ad1905ece2eba05dbcfdede20f010bc80c8ad924d37df3bebac16c.png',
    description: 'Cobertura en Caracas, Valencia, Maracaibo y más',
    time: '5-7 días',
    gradient: 'from-yellow-400/20 via-blue-600/20 to-red-500/20',
  },
  {
    country: 'Colombia',
    flag: '🇨🇴',
    image: 'https://static.prod-images.emergentagent.com/jobs/b35da085-09af-4a55-863c-9eb3e423a8d1/images/084224ebe79e5f5e4c8f87a52beab8e5a5a8b212b5196b0eea443e015d08a786.png',
    description: 'Bogotá, Medellín, Cali, Cartagena y todo el territorio',
    time: '4-6 días',
    gradient: 'from-yellow-400/20 via-blue-600/20 to-red-500/20',
  },
  {
    country: 'Panamá',
    flag: '🇵🇦',
    image: 'https://images.pexels.com/photos/2906490/pexels-photo-2906490.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    description: 'Envíos rápidos a Ciudad de Panamá y todo el país',
    time: '3-5 días',
    gradient: 'from-blue-600/20 to-red-500/20',
  },
  {
    country: 'República Dominicana',
    flag: '🇩🇴',
    image: 'https://static.prod-images.emergentagent.com/jobs/b35da085-09af-4a55-863c-9eb3e423a8d1/images/8c9edd73798e1c158220c464018f4fcd21cebdc4b4ccc1af71ff0ceac19ea553.png',
    description: 'Santo Domingo, Santiago y principales ciudades',
    time: '4-6 días',
    gradient: 'from-blue-800/20 to-red-600/20',
  },
  {
    country: 'Costa Rica',
    flag: '🇨🇷',
    image: 'https://images.pexels.com/photos/2662116/pexels-photo-2662116.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    description: 'San José, Heredia, Alajuela y más destinos',
    time: 'Pronto',
    gradient: 'from-blue-500/20 to-red-500/20',
    comingSoon: true,
  },
  {
    country: 'Nicaragua',
    flag: '🇳🇮',
    image: 'https://images.pexels.com/photos/11498253/pexels-photo-11498253.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    description: 'Managua, Granada, León y principales ciudades',
    time: 'Pronto',
    gradient: 'from-blue-500/20 to-white/20',
    comingSoon: true,
  },
];

const features = [
  { icon: Clock, title: 'Entrega Puntual', desc: 'Cumplimos con los tiempos' },
  { icon: Shield, title: 'Envío Seguro', desc: 'Tu paquete protegido' },
  { icon: Plane, title: 'Envío Aéreo', desc: 'Máxima velocidad' },
];

export const Services = () => {
  return (
    <section 
      id="servicios" 
      className="relative py-24 md:py-32 bg-[#F8FAFC]"
      data-testid="services-section"
    >
      {/* Mascot peeking from top right - positioned below sticky header */}
      <motion.img
        src={MASCOT_URL}
        alt="Mascota"
        className="absolute top-8 right-8 w-24 h-24 object-contain hidden lg:block z-10 drop-shadow-xl"
        initial={{ y: -50, opacity: 0, rotate: -10 }}
        whileInView={{ y: 0, opacity: 1, rotate: 0 }}
        viewport={{ once: true }}
        transition={{ type: "spring", duration: 0.8 }}
        data-testid="services-mascot"
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block bg-[#0066CC]/10 text-[#0066CC] px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Nuestros Destinos
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Enviamos a <span className="text-[#F5A623]">4 países</span> en Latinoamérica
            <span className="block text-base font-medium text-slate-500 mt-2">
              y 2 destinos más <span className="text-[#0066CC] font-semibold">próximamente</span>
            </span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Conectamos familias y negocios con envíos seguros, rápidos y a precios competitivos
          </p>
        </motion.div>
        
        {/* Destinations Grid - Bento Style */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {destinations.map((dest, index) => (
            <motion.div
              key={dest.country}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`service-card group relative bg-white rounded-3xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-xl ${
                index === 0 ? 'lg:col-span-2 lg:row-span-2' : ''
              } ${dest.comingSoon ? 'ring-2 ring-[#F5A623]/40' : ''}`}
              data-testid={`service-card-${dest.country.toLowerCase().replace(' ', '-')}`}
            >
              {/* Coming Soon corner ribbon */}
              {dest.comingSoon && (
                <div className="absolute top-4 -right-10 z-20 rotate-45 bg-gradient-to-r from-[#F5A623] to-[#CC0000] text-white text-xs font-bold py-1 w-40 text-center shadow-lg">
                  <span className="flex items-center justify-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    COMING SOON
                  </span>
                </div>
              )}

              {/* Image */}
              <div className={`relative ${index === 0 ? 'h-64 lg:h-80' : 'h-48'} overflow-hidden`}>
                <img
                  src={dest.image}
                  alt={dest.country}
                  className={`w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 ${
                    dest.comingSoon ? 'grayscale-[60%] group-hover:grayscale-0' : ''
                  }`}
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${dest.gradient} opacity-60`} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                
                {/* Flag badge */}
                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1.5 flex items-center gap-2">
                  <span className="text-xl">{dest.flag}</span>
                  <span className="font-semibold text-slate-800 text-sm">{dest.country}</span>
                </div>
                
                {/* Time badge */}
                <div className={`absolute ${dest.comingSoon ? 'bottom-4' : 'top-4'} ${dest.comingSoon ? 'left-4' : 'right-4'} backdrop-blur-sm rounded-full px-3 py-1.5 flex items-center gap-1 ${
                  dest.comingSoon ? 'bg-[#F5A623]/90' : 'bg-[#2DD4BF]/90'
                }`}>
                  <Clock className="w-3.5 h-3.5 text-white" />
                  <span className="text-white text-sm font-medium">{dest.time}</span>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-5">
                <div className="flex items-start gap-2 mb-2">
                  <MapPin className={`w-4 h-4 mt-1 flex-shrink-0 ${dest.comingSoon ? 'text-[#F5A623]' : 'text-[#CC0000]'}`} />
                  <p className="text-slate-600 text-sm">{dest.description}</p>
                </div>
                
                {dest.comingSoon ? (
                  <div className="mt-3 inline-flex items-center gap-1.5 bg-[#F5A623]/10 text-[#F5A623] font-semibold text-sm px-3 py-1.5 rounded-full">
                    <Sparkles className="w-3.5 h-3.5" />
                    ¡Muy pronto!
                  </div>
                ) : (
                  <button className="mt-3 text-[#0066CC] font-semibold text-sm hover:text-[#0052a3] transition-colors flex items-center gap-1 group/btn">
                    Ver tarifas
                    <span className="group-hover/btn:translate-x-1 transition-transform">→</span>
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </div>
        
        {/* Features Row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {features.map((feature, index) => (
            <div
              key={feature.title}
              className="flex items-center gap-4 bg-white rounded-2xl p-6 border border-slate-100"
              data-testid={`feature-${index}`}
            >
              <div className="w-14 h-14 bg-[#0066CC]/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                <feature.icon className="w-7 h-7 text-[#0066CC]" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">{feature.title}</h3>
                <p className="text-slate-500 text-sm">{feature.desc}</p>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
